
import { UseFormReturn } from "react-hook-form";
import { Subscription } from "@/types/subscription";
import { toast } from "@/hooks/use-toast";
import { usePromoCode } from "@/hooks/usePromoCode";

interface SubscriptionFormSubmissionProps {
  form: UseFormReturn<any>;
  onSubmit: (data: Omit<Subscription, "id" | "totalPaid">) => Promise<Subscription>;
  onSubscriptionCreated: (subscription: Subscription) => void;
  onPaymentError: (error: string | null) => void;
}

export function useSubscriptionFormSubmission({
  form,
  onSubmit,
  onSubscriptionCreated,
  onPaymentError
}: SubscriptionFormSubmissionProps) {
  
  const { applyPromoCode } = usePromoCode();
  
  const handleSubmit = async (data: any) => {
    console.log("=== SUBSCRIPTION FORM SUBMISSION DEBUG ===");
    console.log("Raw form data:", data);
    console.log("isBusiness value:", data.isBusiness, "type:", typeof data.isBusiness);
    console.log("businessName value:", data.businessName, "type:", typeof data.businessName);
    
    // Validate required fields
    if (!data.customerName?.trim()) {
      toast({
        title: "Validation Error",
        description: "Customer name is required",
        variant: "destructive"
      });
      return;
    }

    if (!data.email?.trim()) {
      toast({
        title: "Validation Error", 
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }

    if (!data.address?.trim()) {
      toast({
        title: "Validation Error",
        description: "Address is required", 
        variant: "destructive"
      });
      return;
    }

    if (!data.postcode?.trim()) {
      toast({
        title: "Validation Error",
        description: "Postcode is required",
        variant: "destructive"
      });
      return;
    }

    // Note: Bin collection day is now automatically determined, so no validation needed

    // Business validation
    const isBusiness = data.isBusiness === true;
    if (isBusiness && (!data.businessName || data.businessName.trim() === '')) {
      toast({
        title: "Validation Error",
        description: "Business name is required for business subscriptions",
        variant: "destructive"
      });
      return;
    }
    
    // Handle bin day for multiple days option
    if (data.multipleDays) {
      data.binDay = 'multiple';
    }
    
    const subscriptionData = {
      customerName: data.customerName.trim(),
      email: data.email.trim(),
      address: data.address.trim(),
      postcode: data.postcode.trim(),
      binDay: data.binDay.trim(),
      startDate: new Date().toISOString(),
      isAnnual: Boolean(data.isAnnual),
      isBusiness: isBusiness,
      businessName: isBusiness && data.businessName ? data.businessName.trim() : null,
      multipleDays: Boolean(data.multipleDays),
      promoCode: data.promoCode?.trim() || null,
      discountApplied: 0, // Will be updated if promo code is applied
      active: false, // Start as inactive - will be activated by webhook after payment
      status: 'pending_payment' // Set status to pending payment
    };
    
    console.log("Final subscription data being passed to onSubmit:", subscriptionData);
    console.log("Business fields specifically:", {
      isBusiness: subscriptionData.isBusiness,
      businessName: subscriptionData.businessName,
      isBusinessType: typeof subscriptionData.isBusiness,
      businessNameType: typeof subscriptionData.businessName
    });
    
    // Explicit business validation logging
    if (subscriptionData.isBusiness === true) {
      console.log("🏢 BUSINESS SUBSCRIPTION DETECTED!");
      console.log("Business Name provided:", subscriptionData.businessName);
      console.log("Business Name length:", subscriptionData.businessName ? subscriptionData.businessName.length : 'N/A');
      console.log("Business Name truthy check:", !!subscriptionData.businessName);
      
      if (!subscriptionData.businessName || subscriptionData.businessName.trim() === '') {
        console.error("⚠️ WARNING: Business subscription has empty/null business name!");
        toast({
          title: "Validation Error",
          description: "Business name cannot be empty for business subscriptions",
          variant: "destructive"
        });
        return;
      } else {
        console.log("✅ Business subscription has valid business name:", subscriptionData.businessName);
      }
    } else {
      console.log("👤 PERSONAL SUBSCRIPTION - No business data expected");
    }
    
    console.log("=== END SUBSCRIPTION FORM SUBMISSION DEBUG ===");
    
    try {
      // SECURITY FIX: Don't create subscription in database until payment is confirmed
      // Store subscription data temporarily in the component state instead
      
      // Apply promo code validation if provided
      let discountAmount = 0;
      if (subscriptionData.promoCode) {
        console.log("🎯 Validating promo code:", subscriptionData.promoCode);
        const promoResult = await applyPromoCode(
          subscriptionData.promoCode,
          subscriptionData.email,
          subscriptionData.customerName,
          5.00, // Original amount
          null // No subscription ID yet since we haven't created it
        );
        
        if (promoResult.success) {
          discountAmount = promoResult.discount_applied;
          subscriptionData.discountApplied = discountAmount;
          console.log("✅ Promo code validated successfully, discount:", discountAmount);
        } else {
          console.warn("⚠️ Promo code validation failed:", promoResult.error);
          toast({
            title: "Promo Code Error",
            description: promoResult.error || "Invalid promo code",
            variant: "destructive"
          });
          return;
        }
      }
      
      // Create a temporary subscription object (not saved to database yet)
      const tempSubscription: Subscription = {
        ...subscriptionData,
        id: `temp-${Date.now()}`, // Temporary ID
        totalPaid: 0,
        discountApplied: discountAmount
      };
      
      console.log("=== TEMPORARY SUBSCRIPTION FOR PAYMENT ===");
      console.log("Temp subscription data:", tempSubscription);
      
      // Store subscription data in sessionStorage for payment processing
      sessionStorage.setItem('pendingSubscription', JSON.stringify(subscriptionData));
      
      onSubscriptionCreated(tempSubscription);
      onPaymentError(null);
      
      form.reset();
      toast({
        title: "Ready for payment",
        description: `Please complete your payment to activate the subscription for ${subscriptionData.customerName}.`,
      });
    } catch (error) {
      console.error("Error preparing subscription:", error);
      toast({
        title: "Error",
        description: "Failed to prepare subscription. Please try again.",
        variant: "destructive"
      });
    }
  };

  return { handleSubmit };
}
